import { useEffect, useRef, useState } from 'react';

const scriptPromises = new Map(); // src -> Promise<void>
const globalPromises = new Map(); // globalName -> Promise<any>

interface ScriptAttributes {
  integrity?: string;
  crossOrigin?: string;
  referrerPolicy?: string;
  nonce?: string;
}

// eslint-disable-next-line @typescript-eslint/promise-function-async
export const loadScriptOnce = (src: string, attrs: ScriptAttributes = {}): Promise<void> => {
  if (scriptPromises.has(src)) return scriptPromises.get(src);

  const p = new Promise<void>((resolve, reject) => {
    let el = document.querySelector<HTMLScriptElement>(`script[src="${src}"]`);
    const exists = !!el;

    if (!el) {
      el = document.createElement('script');
      el.src = src;
      el.async = true;
      el.defer = true;

      if (attrs.integrity) el.integrity = attrs.integrity;
      if (attrs.crossOrigin) el.crossOrigin = attrs.crossOrigin;
      if (attrs.referrerPolicy) el.referrerPolicy = attrs.referrerPolicy;
      if (attrs.nonce) el.nonce = attrs.nonce;

      document.head.appendChild(el);
    }

    // Check if script is already loaded
    if (el.getAttribute('data-loaded') === 'true') {
      resolve();
      return;
    }

    const onLoad = () => {
      if (el) {
        el.setAttribute('data-loaded', 'true');
      }
      cleanup();
      resolve();
    };
    const onError = () => {
      cleanup();
      el?.remove();
      scriptPromises.delete(src);
      reject(new Error(`Failed to load script: ${src}`));
    };
    const cleanup = () => {
      if (el) {
        el.removeEventListener('load', onLoad);
        el.removeEventListener('error', onError);
      }
    };

    el.addEventListener('load', onLoad);
    el.addEventListener('error', onError);

    if (exists && el.getAttribute('data-loaded') === 'true') {
      queueMicrotask(() => {
        cleanup();
        resolve();
      });
    }
  });

  scriptPromises.set(src, p);
  return p;
};

const isGlobalValueReady = (globalName: string, isReady?: () => boolean): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  const globalValue = (window as any)[globalName];
  if (!globalValue) {
    return false;
  }

  return !isReady || isReady();
};

// eslint-disable-next-line @typescript-eslint/promise-function-async
const waitForGlobal = <T = any>(
  globalName: string,
  timeoutMs = 15000,
  intervalMs = 50,
  isReady?: () => boolean,
): Promise<T> => {
  const cacheKey = isReady ? undefined : globalName;
  if (cacheKey && globalPromises.has(cacheKey)) {
    return globalPromises.get(cacheKey);
  }

  const p = new Promise<T>((resolve, reject) => {
    const start = Date.now();
    const check = () => {
      if (isGlobalValueReady(globalName, isReady)) {
        resolve((window as any)[globalName]);
        return;
      }
      if (Date.now() - start >= timeoutMs) {
        if (cacheKey) {
          globalPromises.delete(cacheKey);
        }
        const detail = isReady ? ' (readiness check failed)' : '';
        reject(new Error(`Global "${globalName}" not found after ${timeoutMs}ms${detail}`));
        return;
      }
      setTimeout(check, intervalMs);
    };
    check();
  });

  if (cacheKey) {
    globalPromises.set(cacheKey, p);
  }
  return p;
};

interface UseScriptGlobalOptions {
  src: string;
  globalName: string;
  attrs?: ScriptAttributes;
  timeoutMs?: number;
  /** When set, loading finishes only after this returns true (primary global must also exist). */
  isReady?: () => boolean;
}

interface UseScriptGlobalReturn<T> {
  value: T | undefined;
  loading: boolean;
  error: Error | null;
}

export const useScriptGlobal = <T = any>({
  src,
  globalName,
  attrs,
  timeoutMs = 15000,
  isReady,
}: UseScriptGlobalOptions): UseScriptGlobalReturn<T> => {
  const [value, setValue] = useState<T | undefined>(() =>
    typeof window !== 'undefined' && isGlobalValueReady(globalName, isReady) ? (window as any)[globalName] : undefined,
  );
  const [loading, setLoading] = useState(() =>
    typeof window === 'undefined' ? false : !isGlobalValueReady(globalName, isReady),
  );
  const [error, setError] = useState<Error | null>(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    if (isGlobalValueReady(globalName, isReady)) {
      setValue((window as any)[globalName]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        await loadScriptOnce(src, attrs);
        const g = await waitForGlobal<T>(globalName, timeoutMs, 50, isReady);
        if (!cancelled && mounted.current) {
          setValue(g);
          setLoading(false);
        }
      } catch (e) {
        if (!cancelled && mounted.current) {
          setError(e instanceof Error ? e : new Error(String(e)));
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [src, globalName, timeoutMs, attrs, isReady]);

  return { value, loading, error };
};
